/**
 * VideoSlide - a single full-screen slide in the vertical feed.
 *
 * Presentational shell around one <video>: poster fade-in, buffering
 * preloader, tap-to-pause overlay, scrub bar, caption and the action rail.
 * The <video> element registers itself into the engine's ref array via
 * `registerRef`. All interaction is delegated up to the feed.
 */
import { useState, useEffect, useMemo } from 'preact/hooks';
import { ActionRail } from './ActionRail.jsx';
import { Caption } from './Caption.jsx';
import { Scrubber } from './Scrubber.jsx';

const DEFAULT_STATE = {
  ready: false,
  progress: 0,
  paused: false,
  posterFaded: false,
  stalled: false,
  loadPct: 0,
};

export function VideoSlide({
  video,
  state = DEFAULT_STATE,
  isActive,
  muted,
  top,
  poster,
  avatar,
  accent,
  registerRef,
  media, // { onCanPlay, onPlaying, onWaiting, onProgress, onLoadedMetadata, onTimeUpdate, onError }
  pointer, // { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick }
  onSeek, // (fraction, committed) => void
  onScrubActive,
  likeCount,
  isFavorite,
  showChat,
  immersive,
  onLike,
  onToggleFavorite,
  onChat,
  onShare,
  onToggleImmersive,
}) {
  const i = video.actualIndex;
  const [captionOpen, setCaptionOpen] = useState(false);

  // Debounce onSeek by 100ms to avoid excessive updates during scrubbing
  const debouncedOnSeek = useMemo(() => {
    let timeout;
    return (fraction, committed) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => onSeek(fraction, committed), 100);
    };
  }, [onSeek]);
  const hasCaption = !!(video.title || video.author || video.caption);

  // Debounce the buffering preloader by 300ms: only reveal it if the slide is
  // still not ready/stalled after the delay, so fast scrubbing doesn't flash it.
  const needsPreloader = isActive && (!state.ready || state.stalled);
  const [showPreloader, setShowPreloader] = useState(false);
  useEffect(() => {
    if (!needsPreloader) {
      setShowPreloader(false);
      return undefined;
    }
    const t = window.setTimeout(() => setShowPreloader(true), 300);
    return () => window.clearTimeout(t);
  }, [needsPreloader]);

  return (
    <div
      class="slide"
      data-index={i}
      style={{
        transform: `translateY(${top}px)`,
        background: accent || '#000',
      }}
    >
      {showPreloader && (
        <div class="slide__preloader">
          <span class="slide__preloader-pct">{state.loadPct || 0}%</span>
        </div>
      )}

      <video
        ref={registerRef}
        class="slide__video"
        controlsList="nodownload nofullscreen"
        muted={!isActive || muted}
        loop
        playsInline
        onCanPlay={media.onCanPlay}
        onPlaying={media.onPlaying}
        onWaiting={media.onWaiting}
        onProgress={media.onProgress}
        onLoadedMetadata={media.onLoadedMetadata}
        onTimeUpdate={media.onTimeUpdate}
        onError={media.onError}
        onPointerDown={pointer.onPointerDown}
        onPointerMove={pointer.onPointerMove}
        onPointerUp={pointer.onPointerUp}
        onPointerCancel={pointer.onPointerCancel}
        onClick={pointer.onClick}
      />

      {poster && (
        <img
          class={`slide__poster ${state.posterFaded ? 'slide__poster--faded' : ''}`}
          src={poster}
          alt=""
          decoding="async"
          fetchpriority={isActive ? 'high' : 'auto'}
        />
      )}

      <div class="slide__scrim" />

      {state.paused && isActive && (
        <div class="slide__pause" aria-hidden="true">
          <span class="slide__pause-icon">▶</span>
        </div>
      )}

      <ActionRail
        video={video}
        avatar={avatar}
        likeCount={likeCount}
        isFavorite={isFavorite}
        showChat={showChat}
        immersive={immersive}
        onLike={onLike}
        onToggleFavorite={onToggleFavorite}
        onChat={onChat}
        onShare={onShare}
        onToggleImmersive={onToggleImmersive}
      />

      {!immersive && (
      <div class="slide__bottom">
        {captionOpen && hasCaption && (
          <div class="slide__caption-pop">
            <Caption video={video} />
          </div>
        )}
        <div class="slide__controls">
          {hasCaption && (
            <button
              type="button"
              class={`slide__info ${captionOpen ? 'slide__info--open' : ''}`}
              aria-label="Video info"
              aria-expanded={captionOpen ? 'true' : 'false'}
              onClick={(e) => {
                e.stopPropagation();
                setCaptionOpen((o) => !o);
              }}
            >
              i
            </button>
          )}
          <Scrubber
            progress={state.progress || 0}
            onSeek={debouncedOnSeek}
            onActiveChange={onScrubActive}
          />
        </div>
      </div>
      )}
    </div>
  );
}
